<script setup>
// NOTICE: KAPTION-INTEGRATION — group-visibility.GUARDRAIL.1/.2/.3.
//
// Shown under the agent picker when the people being added to this team have an
// EFFECTIVE visibility wider than the team's classification implies. Warns in both
// directions:
//
//   adding a restricted-group member to an OPEN team        -> they are not isolated here
//   adding someone in an open group to a RESTRICTED team    -> this does not isolate them
//
// Confirm-to-proceed, never a block: multi-group membership and union visibility are
// the intended behaviour (group-visibility.MULTI_GROUP.1/.2), so refusing the action
// would be wrong. The failure mode this closes is an admin making the arrangement
// WITHOUT KNOWING they made it.
//
// On the copy: Phase 0 measured that open-team visibility follows INBOX membership,
// not membership of the open team, so this deliberately does NOT say "adding them
// here lets them see X". It states what they can actually see and points at the real
// lever. See useKaptionGroupVisibility.js for the measured semantics.
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useKaptionGroupVisibility } from 'dashboard/composables/useKaptionGroupVisibility';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  agentIds: { type: Array, default: () => [] },
  agentList: { type: Array, default: () => [] },
  teamVisibility: { type: String, default: '' },
  teamName: { type: String, default: '' },
});

const { t } = useI18n();
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const { fetchAgentGroups, visibilityWarningsFor } = useKaptionGroupVisibility();

const dialogRef = ref(null);
const pendingResolve = ref(null);

const isFlagEnabled = computed(() =>
  isKaptionFlagEnabled('KAPTION_GROUP_VISIBILITY')
);

const nameFor = userId =>
  props.agentList.find(agent => agent.id === userId)?.name || '';

const warnings = computed(() => {
  if (!isFlagEnabled.value) return [];
  return visibilityWarningsFor({
    agentIds: props.agentIds,
    teamVisibility: props.teamVisibility,
    teamName: props.teamName,
  }).map(warning => ({
    ...warning,
    body: t(
      warning.direction === 'open'
        ? 'TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.OPEN_BODY'
        : 'TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.RESTRICTED_BODY',
      {
        name: nameFor(warning.userId),
        groups: warning.groupNames,
        team: warning.teamName,
      }
    ),
  }));
});

const hasWarnings = computed(() => warnings.value.length > 0);

onMounted(() => {
  if (isFlagEnabled.value) fetchAgentGroups();
});

watch(
  () => props.teamVisibility,
  visibility => {
    if (visibility && isFlagEnabled.value) fetchAgentGroups();
  }
);

/**
 * Returns a Promise<boolean>. Resolves true immediately when there is nothing to
 * warn about, so callers can await it unconditionally.
 */
const requestConfirmation = () => {
  if (!hasWarnings.value) return Promise.resolve(true);

  return new Promise(resolve => {
    pendingResolve.value = resolve;
    dialogRef.value?.open();
  });
};

const settle = outcome => {
  const resolve = pendingResolve.value;
  pendingResolve.value = null;
  if (resolve) resolve(outcome);
};

defineExpose({ requestConfirmation, hasWarnings, warnings });
</script>

<template>
  <div v-if="hasWarnings">
    <div
      class="mt-3 rounded-xl border border-n-amber-8 bg-n-amber-3 p-3 text-sm text-n-slate-12"
      data-testid="visibility-warning"
    >
      <h5 class="font-medium text-n-amber-11 mb-1">
        {{ $t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.TITLE') }}
      </h5>
      <ul class="list-disc ltr:ml-5 rtl:mr-5 mb-1">
        <li v-for="warning in warnings" :key="warning.userId">
          {{ warning.body }}
        </li>
      </ul>
      <p class="text-n-slate-11 text-xs mb-0">
        {{ $t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.INBOX_NOTE') }}
      </p>
    </div>

    <Dialog
      ref="dialogRef"
      type="alert"
      :title="$t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.TITLE')"
      :description="$t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.INBOX_NOTE')"
      :confirm-button-label="
        $t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.CONFIRM')
      "
      :cancel-button-label="
        $t('TEAMS_SETTINGS.AGENTS.VISIBILITY_WARNING.CANCEL')
      "
      @confirm="settle(true)"
      @close="settle(false)"
    >
      <ul class="list-disc ltr:ml-5 rtl:mr-5 text-sm text-n-slate-12">
        <li v-for="warning in warnings" :key="warning.userId">
          {{ warning.body }}
        </li>
      </ul>
    </Dialog>
  </div>
</template>
